import { ICustomerSocialMediaDTO } from "@hooks/dtos/customer/ICustomerSocialMediaDTO";
import {
  FaInstagram,
  FaFacebook,
  FaTiktok,
  FaTwitch,
  FaXTwitter,
  FaLink,
} from "react-icons/fa6";
import { IconType } from "react-icons";
import { SocialMediaTypeIdEnum } from "@hooks/dtos/ISocialMediaTypeDTO";

interface IProps {
  socialMedia: ICustomerSocialMediaDTO;
  className?: string;
  onClick?: (socialMedia: ICustomerSocialMediaDTO) => void;
}

export const socialMediaConfig = {
  [SocialMediaTypeIdEnum.INSTAGRAM]: {
    icon: FaInstagram,
    color: "#E4405F",
    bgLight: "#FCE4EC",
  },
  [SocialMediaTypeIdEnum.FACEBOOK]: {
    icon: FaFacebook,
    color: "#1877F2",
    bgLight: "#E3F2FD",
  },
  [SocialMediaTypeIdEnum.X]: {
    icon: FaXTwitter,
    color: "#000000",
    bgLight: "#F5F5F5",
  },
  [SocialMediaTypeIdEnum.TIKTOK]: {
    icon: FaTiktok,
    color: "#000000",
    bgLight: "#F5F5F5",
  },
  [SocialMediaTypeIdEnum.TWITCH]: {
    icon: FaTwitch,
    color: "#9146FF",
    bgLight: "#EDE7F6",
  },
};

// Colors using Bootstrap classes only
const getBootstrapBadgeColor = (typeId: number): string => {
  switch (typeId) {
    case SocialMediaTypeIdEnum.INSTAGRAM:
      return "text-white";
    case SocialMediaTypeIdEnum.FACEBOOK:
      return "bg-primary text-white";
    case SocialMediaTypeIdEnum.X:
      return "bg-dark text-white";
    case SocialMediaTypeIdEnum.TIKTOK:
      return "bg-black text-white";
    case SocialMediaTypeIdEnum.TWITCH:
      return "twitch-bg text-white";
    default:
      return "bg-secondary text-white";
  }
};

const getSocialMediaIcon = (typeId: number): IconType => {
  switch (typeId) {
    case SocialMediaTypeIdEnum.INSTAGRAM:
      return FaInstagram;
    case SocialMediaTypeIdEnum.FACEBOOK:
      return FaFacebook;
    case SocialMediaTypeIdEnum.X:
      return FaXTwitter;
    case SocialMediaTypeIdEnum.TIKTOK:
      return FaTiktok;
    case SocialMediaTypeIdEnum.TWITCH:
      return FaTwitch;
    default:
      return FaLink;
  }
};

export const SocialMediaBadge = ({
  socialMedia,
  className = "",
  onClick,
}: IProps) => {
  const IconComponent = getSocialMediaIcon(socialMedia.type);
  const badgeColor = getBootstrapBadgeColor(socialMedia.type);

  const handleClick = () => {
    if (onClick) {
      onClick(socialMedia);
    }

    if (socialMedia.url) {
      window.open(socialMedia.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <span
      className={`${badgeColor} ${className} rounded d-inline-flex align-items-center fs-6 px-3 py-1`}
      style={{
        cursor: socialMedia.url ? "pointer" : "default",
        // Inline style as fallback
        ...(socialMedia.type === SocialMediaTypeIdEnum.INSTAGRAM && {
          background:
            "linear-gradient(45deg, #405DE6, #C13584, #FD1D1D, #FCAF45)",
        }),
        ...(socialMedia.type === SocialMediaTypeIdEnum.TWITCH && {
          backgroundColor: "#9146FF",
        }),
      }}
      onClick={socialMedia.url ? handleClick : undefined}
      title={
        socialMedia.url
          ? `Visitar ${socialMedia.typeData.name} do Usuário.`
          : socialMedia.typeData.name
      }
    >
      <IconComponent className="fs-5 me-1" />
      <span>{socialMedia.typeData.name}</span>
    </span>
  );
};

// Get social media icon component
export const getSocialMediaConfigIcon = (typeId: number) => {
  const config = socialMediaConfig[typeId as keyof typeof socialMediaConfig];
  if (!config) return null;
  const IconComponent = config.icon;
  return <IconComponent size={24} style={{ color: config.color }} />;
};

// Get social media background color
export const getSocialMediaConfigBg = (typeId: number) => {
  const config = socialMediaConfig[typeId as keyof typeof socialMediaConfig];
  return config?.bgLight || "#F5F5F5";
};
